// Preserve recorded punch times and surface unresolved shifts without inventing hours.
export const REVIEW_AFTER_MS = 24 * 60 * 60 * 1000;

export async function getOpenEntries(client, employeeIds) {
  if (!employeeIds.length) return [];
  const { data, error } = await client.from('t4s_time_entries')
    .select('id,employee_id,job_id,work_date,clock_in,clock_out,total_hours,status,updated_at,job:t4s_jobs!job_id(name,job_number),allocations:t4s_time_allocations(id,cost_code_id,hours,cost_code:t4s_cost_codes(id,code,name))')
    .in('employee_id', employeeIds).in('status', ['active', 'pending'])
    .is('clock_out', null).not('clock_in', 'is', null).limit(500);
  if (error) throw error;
  if ((data || []).length >= 500) throw new Error('Too many open shifts. Ask the office to review them before clocking in.');
  return data || [];
}

export function needsOfficeReview(entry, now = Date.now()) {
  const started = Date.parse(entry.clock_in);
  return !Number.isFinite(started) || now - started >= REVIEW_AFTER_MS;
}

export function openShiftMessage(entry) {
  return `Open shift from ${entry.work_date} at ${entry.job?.job_number || ''} ${entry.job?.name || 'another job'}. Ask the office to review the missing clock-out before starting another shift.`;
}

export async function assertNoOpenEntries(client, employeeIds) {
  const entries = await getOpenEntries(client, employeeIds);
  if (entries.length) throw new Error(openShiftMessage(entries[0]));
}

export async function saveClockOut(client, timeEntryId, details = {}, now = Date.now()) {
  const clockOut = details.clockOut || new Date(now).toISOString();
  const read = async () => {
    const { data, error } = await client.from('t4s_time_entries').select('*').eq('id', timeEntryId).single();
    if (error) throw error;
    return data;
  };
  const entry = await read();
  // A lost acknowledgement or repeated tap must not move an existing end time.
  if (entry.clock_out) return entry;
  const end = Date.parse(clockOut), start = Date.parse(entry.clock_in);
  if (!entry.updated_at || !['active', 'pending'].includes(entry.status) || !Number.isFinite(start) || !Number.isFinite(end) || end <= start || end > now) {
    throw new Error('This shift needs review. Refresh before clocking out.');
  }
  // Queued overnight punches use the recorded event time, not the later sync time.
  if (needsOfficeReview(entry, end)) throw new Error('This shift has been open for 24 hours or more. Ask the office to enter the verified clock-out and paid hours.');
  const { data, error } = await client.from('t4s_time_entries').update({
    clock_out: new Date(end).toISOString(),
    clock_out_latitude: details.latitude,
    clock_out_longitude: details.longitude,
    clock_out_photo_url: details.photoUrl,
  }).eq('id', timeEntryId).eq('updated_at', entry.updated_at).is('clock_out', null)
    .in('status', ['active', 'pending']).select().maybeSingle();
  if (error) throw error;
  if (data) return data;
  const current = await read();
  if (current.clock_out) return current;
  throw new Error('The shift changed before clock-out was saved. Refresh and review it.');
}
